
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { InventoryItem } from '@/types/inventory';
import { useInventoryConnection } from './useInventoryConnection';
import { useWarehouseSelection } from './useWarehouseSelection';
import { useViewModeToggle, ViewMode } from './useViewModeToggle';
import { useInventorySearch } from './useInventorySearch';

// Change the re-export to use 'export type' instead of just 'export'
export type { ViewMode } from './useViewModeToggle';

export function useInventoryLiveSearch() {
  // Use our separated hooks
  const connection = useInventoryConnection();
  const warehouse = useWarehouseSelection();
  const viewMode = useViewModeToggle('list');
  const search = useInventorySearch({ 
    lastUpdate: connection.lastUpdate, 
    setLastUpdate: connection.setLastUpdate 
  });

  // Trigger search when search term or warehouse changes
  useEffect(() => {
    // Skip when no search term AND no warehouse filter
    if (search.debouncedSearch.length === 0 && warehouse.selectedWarehouse === null) {
      search.clearResults();
      return;
    }

    // Perform search directly - debouncing already handled by debouncedSearch
    const controller = new AbortController();
    
    search.performSearch(search.debouncedSearch, warehouse.selectedWarehouse)
      .catch(error => {
        if (!controller.signal.aborted) {
          console.error('Search error:', error);
        }
      });
    
    return () => controller.abort();
  }, [search.debouncedSearch, warehouse.selectedWarehouse, search.performSearch]);

  // Load warehouses on initial mount
  useEffect(() => {
    warehouse.loadWarehouses();
  }, []);

  return { 
    searchTerm: search.searchTerm, 
    setSearchTerm: search.setSearchTerm, 
    results: search.results, 
    isLoading: connection.isLoading || search.isLoading || warehouse.isLoading, 
    lastUpdate: connection.lastUpdate, 
    sampleProducts: connection.sampleProducts, 
    connectionVerified: connection.connectionVerified,
    availableWarehouses: warehouse.availableWarehouses,
    selectedWarehouse: warehouse.selectedWarehouse,
    setSelectedWarehouse: warehouse.selectWarehouse,
    reloadWarehouses: warehouse.refreshWarehouses,
    viewMode: viewMode.viewMode,
    toggleViewMode: viewMode.toggleViewMode
  };
}
